import { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtDate, fmtDateUS } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Phone, MapPin, Calendar, AlertTriangle, X, Trash2, Send, Lock, Upload, Download, TrendingUp, Check, Sparkles } from "lucide-react";
import { PageTip } from "@/components/Onboarding";
import PullToRefresh from "@/components/PullToRefresh";
import BottomSheet from "@/components/BottomSheet";
import ConfirmDialog from "@/components/ConfirmDialog";
import LeadActivityLog from "@/components/LeadActivityLog";
import LeadAppointments from "@/components/LeadAppointments";
import LeadTasks from "@/components/LeadTasks";

export const LEAD_STATUSES = [
  { value: "new", label: "New", color: "#9ca3af" },
  { value: "appointment_set", label: "Appt Set", color: "#a78bfa" },
  { value: "offer_made", label: "Offer Made", color: "#fbbf24" },
  { value: "under_contract", label: "Under Contract", color: "#C5E26A" },
  { value: "closed", label: "Closed", color: "#22c55e" },
  { value: "dead", label: "Dead", color: "#71717a" },
];
export const LEAD_TERMINAL_STATUSES = ["under_contract", "closed", "dead"];
export const LEAD_SOURCES = [
  { value: "cold_call", label: "Cold Call" },
  { value: "fsbo", label: "FSBO" },
  { value: "sms_blast", label: "SMS Blast" },
  { value: "ppl", label: "PPL" },
  { value: "ppc", label: "PPC" },
  { value: "driving_for_dollars", label: "Driving for Dollars" },
  { value: "door_knock", label: "Door Knocking" },
  { value: "referral", label: "Referral" },
  { value: "zillow", label: "Zillow / Agent" },
  { value: "facebook", label: "Facebook / Marketplace" },
  { value: "direct_mail", label: "Direct Mail" },
  { value: "probate", label: "Probate / Inherited" },
  { value: "other", label: "Other" },
];

const statusLabel = (s) => LEAD_STATUSES.find((x) => x.value === s)?.label || s;
const statusColor = (s) => LEAD_STATUSES.find((x) => x.value === s)?.color || "#9ca3af";
const sourceLabel = (s) => LEAD_SOURCES.find((x) => x.value === s)?.label || s;

export default function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [hoverStage, setHoverStage] = useState(null);

  const moveLeadToStage = async (leadId, newStatus) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.status === newStatus) return;
    // Optimistic update
    setLeads((arr) => arr.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));
    try {
      await api.patch(`/leads/${leadId}`, { status: newStatus });
    } catch (err) {
      // Revert on error
      setLeads((arr) => arr.map((l) => (l.id === leadId ? { ...l, status: lead.status } : l)));
      const { toast } = await import("sonner");
      toast.error("Could not move lead");
    }
  };

  const PRIVACY_ACK_KEY = "wu_leads_privacy_acked";
  const hasAckedPrivacy = () => localStorage.getItem(PRIVACY_ACK_KEY) === "1";

  const requestAdd = () => {
    if (!hasAckedPrivacy()) {
      setShowPrivacy(true);
      return;
    }
    setShowAdd(true);
  };

  const ackPrivacy = () => {
    localStorage.setItem(PRIVACY_ACK_KEY, "1");
    setShowPrivacy(false);
    setShowAdd(true);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (e) {
      toast.error("Could not load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchLeads = async () => {
      try {
        const r = await api.get("/leads");
        if (mounted) setLeads(r.data);
      } catch {
        if (mounted) toast.error("Could not load leads");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchLeads();
    return () => { mounted = false; };
  }, []);

  const [clearing, setClearing] = useState(false);
  const today = new Date().toISOString().split("T")[0];
  const followupsDue = useMemo(
    () => leads.filter((l) => l.next_follow_up && l.next_follow_up <= today && !["under_contract", "closed", "dead"].includes(l.status)),
    [leads, today]
  );

  const clearFollowup = async (lead) => {
    try {
      await api.patch(`/leads/${lead.id}`, { next_follow_up: null });
      setLeads((arr) => arr.map((l) => (l.id === lead.id ? { ...l, next_follow_up: null } : l)));
      toast.success("Follow-up cleared", { description: lead.seller_name });
    } catch (e) {
      toast.error("Could not clear that follow-up");
    }
  };

  const clearAllFollowups = async () => {
    setClearing(true);
    try {
      const { data } = await api.post("/leads/followups-due/clear");
      const dueIds = new Set(followupsDue.map((l) => l.id));
      setLeads((arr) => arr.map((l) => (dueIds.has(l.id) ? { ...l, next_follow_up: null } : l)));
      toast.success(`Cleared ${data.cleared} follow-up${data.cleared === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error("Could not clear follow-ups");
    } finally {
      setClearing(false);
    }
  };

  const byStatus = useMemo(() => {
    const m = {};
    LEAD_STATUSES.forEach((s) => { m[s.value] = []; });
    leads.forEach((l) => { (m[l.status] || m.new).push(l); });
    return m;
  }, [leads]);

  const funnel = useMemo(() => {
    const n = byStatus.new.length;
    const a = byStatus.appointment_set.length;
    const o = byStatus.offer_made.length;
    const uc = byStatus.under_contract.length;
    const c = byStatus.closed.length;
    const qualified = n + a + o + uc + c; // exclude dead from funnel math
    const pct = (num, denom) => (denom > 0 ? Math.round((num / denom) * 100) : 0);
    const stat = (num, denom) => ({ pct: pct(num, denom), num, denom });
    return {
      qualified,
      enough: qualified >= 10,
      newToAppt: stat(a + o + uc + c, qualified),
      apptToOffer: stat(o + uc + c, a + o + uc + c),
      offerToContract: stat(uc + c, o + uc + c),
      closeRate: stat(c, qualified),
    };
  }, [byStatus]);

  const exportCSV = async () => {
    try {
      const res = await api.get("/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch (e) {
      toast.error("Export failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const importCSV = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/leads/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      if (data.imported === 0 && data.skipped > 0) {
        toast.error(`No leads imported`, {
          description: `${data.skipped} row(s) skipped — missing a name or address column. First issue: ${(data.errors && data.errors[0]) || ""}`,
        });
      } else if (data.imported > 0) {
        toast.success(`Imported ${data.imported} lead${data.imported === 1 ? "" : "s"}`, {
          description: data.skipped > 0 ? `${data.skipped} row(s) skipped (missing name/address)` : undefined,
        });
      } else {
        toast.error("No rows found in CSV");
      }
      load();
    } catch (e) {
      toast.error("Import failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <PullToRefresh onRefresh={load}>
    <div data-testid="leads-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="section-tag mb-3"><span className="dot" /> Lead Pipeline</div>
          <h1 className="h-display text-3xl sm:text-4xl">My Leads</h1>
          <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>
            Track sellers from first call to signed contract.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="file"
            accept=".csv,text/csv"
            id="csv-import-input"
            className="hidden"
            onChange={(e) => { importCSV(e.target.files?.[0]); e.target.value = ""; }}
            data-testid="csv-import-input"
          />
          <label
            htmlFor="csv-import-input"
            className="btn-ghost text-sm flex items-center gap-2 cursor-pointer"
            data-testid="csv-import-button"
          >
            <Upload size={14} /> Import CSV
          </label>
          <button onClick={exportCSV} className="btn-ghost text-sm flex items-center gap-2" data-testid="csv-export-button" disabled={leads.length === 0}>
            <Download size={14} /> Export CSV
          </button>          <button onClick={requestAdd} className="btn-primary flex items-center gap-2 text-sm" data-testid="add-lead-button">
            <Plus size={14} /> Add Lead
          </button>
        </div>
      </div>

      {/* Privacy is now a one-time modal shown before first lead — banner removed for cleaner UI */}

      <PageTip flag="leads" title="Your private pipeline">
        Drop in every seller you talk to. On a computer, drag cards between stages. On your phone, tap a
        lead and change its stage. Either way we auto set a 7 day follow up reminder each time you advance.
        Your coach can&apos;t see leads here.
      </PageTip>

      {followupsDue.length > 0 && (
        <div className="surface-card p-5 mb-6" data-testid="followups-due-banner">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} style={{ color: "var(--lime)" }} />
              <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
                {followupsDue.length} follow-up{followupsDue.length === 1 ? "" : "s"} due today or overdue
              </span>
            </div>
            <button
              onClick={clearAllFollowups}
              disabled={clearing}
              className="font-mono text-xs transition hover:opacity-80 whitespace-nowrap"
              style={{ color: "var(--text-muted)" }}
              data-testid="clear-all-followups"
            >
              {clearing ? "Clearing..." : "Clear all"}
            </button>
          </div>
          <div className="space-y-1.5">
            {followupsDue.slice(0, 5).map((l) => (
              <div key={l.id} className="flex items-center gap-2">
                <button onClick={() => setEditing(l)} className="flex-1 min-w-0 text-left text-sm truncate hover:underline" data-testid={`followup-due-${l.id}`}>
                  <span style={{ color: "var(--text-muted)" }}>{fmtDateUS(l.next_follow_up)}</span>
                  {" — "}
                  <span>{l.seller_name}</span>
                  <span style={{ color: "var(--text-muted)" }}> · {l.property_address}</span>
                </button>
                <button
                  onClick={() => clearFollowup(l)}
                  className="flex-shrink-0 p-1.5 rounded-lg transition hover:bg-white/10"
                  style={{ color: "var(--text-dim)" }}
                  title="Mark this follow-up done"
                  data-testid={`clear-followup-${l.id}`}
                >
                  <Check size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : leads.length === 0 ? (
        <div className="surface-card p-12 text-center">
          <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>No leads yet. Start tracking your seller conversations.</p>
          <button onClick={requestAdd} className="btn-primary inline-flex items-center gap-2" data-testid="empty-add-lead-button">
            <Plus size={14} /> Add your first lead
          </button>
        </div>
      ) : (
        <>
          {/* Funnel conversion stats */}
          {funnel.qualified > 0 && (
            <div className="surface-card p-4 mb-4" data-testid="leads-funnel-stats">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={14} style={{ color: "var(--lime)" }} />
                <h3 className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
                  Funnel · {funnel.qualified} active lead{funnel.qualified === 1 ? "" : "s"}
                </h3>
              </div>
              {funnel.enough ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <FunnelStat label="Close Rate" stat={funnel.closeRate} highlight />
                  <FunnelStat label="New → Appt" stat={funnel.newToAppt} />
                  <FunnelStat label="Appt → Offer" stat={funnel.apptToOffer} />
                  <FunnelStat label="Offer → Contract" stat={funnel.offerToContract} />
                </div>
              ) : (
                <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="funnel-needs-more-leads">
                  Add {10 - funnel.qualified} more lead{10 - funnel.qualified === 1 ? "" : "s"} to see your conversion rates. Percentages off a handful of leads don't mean anything yet.
                </p>
              )}
            </div>
          )}

          <div className="overflow-x-auto -mx-2 px-2 pb-2">
          <div className="grid grid-cols-6 gap-3 lead-lanes">
            {LEAD_STATUSES.map((s) => {
              const isHover = hoverStage === s.value && draggedId !== null;
              return (
                <div
                  key={s.value}
                  className="surface-card p-4 transition"
                  style={{
                    borderColor: isHover ? s.color : undefined,
                    background: isHover ? `${s.color}11` : undefined,
                  }}
                  data-testid={`lane-${s.value}`}
                  onDragOver={(e) => {
                    if (draggedId) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      if (hoverStage !== s.value) setHoverStage(s.value);
                    }
                  }}
                  onDragLeave={() => {
                    if (hoverStage === s.value) setHoverStage(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const id = e.dataTransfer.getData("text/plain") || draggedId;
                    if (id) moveLeadToStage(id, s.value);
                    setHoverStage(null);
                    setDraggedId(null);
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} />
                      <span className="font-mono text-xs uppercase tracking-wider truncate">{s.label}</span>
                    </div>
                    <span className="font-mono text-xs shrink-0" style={{ color: "var(--text-muted)" }}>{byStatus[s.value].length}</span>
                  </div>
                  <div className="space-y-2 min-h-[60px]">
                    {byStatus[s.value].map((l) => (
                      <LeadCard
                        key={l.id}
                        lead={l}
                        onClick={() => setEditing(l)}
                        onDragStart={(e) => {
                          setDraggedId(l.id);
                          e.dataTransfer.setData("text/plain", l.id);
                          e.dataTransfer.effectAllowed = "move";
                          // Use the lead card itself as the drag image.
                          // Hand-positioned so the preview tracks under the finger / cursor.
                          try {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const offsetX = (e.clientX || rect.left + rect.width / 2) - rect.left;
                            const offsetY = (e.clientY || rect.top + 20) - rect.top;
                            e.dataTransfer.setDragImage(e.currentTarget, offsetX, offsetY);
                          } catch (err) { /* older browsers */ }
                        }}
                        onDragEnd={() => { setDraggedId(null); setHoverStage(null); }}
                        isDragging={draggedId === l.id}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        </>
      )}

      {showPrivacy && <PrivacyModal onAck={ackPrivacy} onClose={() => setShowPrivacy(false)} />}

      {showAdd && (
        <LeadModal
          onClose={() => setShowAdd(false)}
          onSaved={(l) => { setLeads((arr) => [l, ...arr]); setShowAdd(false); }}
        />
      )}
      {editing && (
        <LeadModal
          lead={editing}
          onClose={() => setEditing(null)}
          onSaved={(l) => { setLeads((arr) => arr.map((x) => x.id === l.id ? l : x)); setEditing(null); }}
          onDelete={(id) => { setLeads((arr) => arr.filter((x) => x.id !== id)); setEditing(null); }}
          onConvert={(id) => navigate(`/submit?lead_id=${id}&type=jv`)}
        />
      )}
    </div>
    </PullToRefresh>
  );
}

function FunnelStat({ label, stat, highlight }) {
  return (
    <div
      className="p-3 rounded-2xl"
      style={{
        background: highlight ? "var(--lime-glow)" : "var(--surface2)",
        border: `1px solid ${highlight ? "var(--border-lime)" : "var(--border)"}`,
      }}
    >
      <div className="font-mono text-[10px] uppercase tracking-wider mb-1" style={{ color: highlight ? "var(--lime)" : "var(--text-muted)" }}>
        {label}
      </div>
      <div className="font-mono text-xl font-semibold" style={{ color: highlight ? "var(--lime)" : "var(--text)" }}>
        {stat.pct}%
      </div>
      <div className="font-mono text-[10px] mt-0.5" style={{ color: "var(--text-dim)" }}>
        {stat.num} of {stat.denom}
      </div>
    </div>
  );
}

function LeadCard({ lead, onClick, onDragStart, onDragEnd, isDragging }) {
  const today = new Date().toISOString().split("T")[0];
  const overdue = lead.next_follow_up && lead.next_follow_up <= today && !["under_contract", "closed", "dead"].includes(lead.status);
  return (
    <button
      onClick={onClick}
      draggable={!!onDragStart}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className="block w-full text-left p-3 rounded-2xl transition hover:bg-white/5 cursor-grab active:cursor-grabbing lead-card"
      style={{
        background: "var(--surface2)",
        border: `1px solid ${overdue ? "var(--lime)" : "var(--border)"}`,
        opacity: isDragging ? 0.4 : 1,
      }}
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="font-medium text-sm truncate mb-1">{lead.seller_name}</div>
      <div className="flex items-center gap-1 text-xs mb-1" style={{ color: "var(--text-muted)" }}>
        <MapPin size={11} />
        <span className="truncate">{lead.property_address}</span>
      </div>
      {lead.seller_phone && (
        <div className="flex items-center gap-1 text-xs" style={{ color: "var(--text-muted)" }}>
          <Phone size={11} /> {lead.seller_phone}
        </div>
      )}
      {lead.next_follow_up && (
        <div className="flex items-center gap-1 text-xs mt-1" style={{ color: overdue ? "var(--lime)" : "var(--text-muted)" }}>
          <Calendar size={11} /> {fmtDateUS(lead.next_follow_up)}
        </div>
      )}
      {lead.asking_price && (
        <div className="font-mono text-xs mt-1" style={{ color: "var(--lime)" }}>{fmtMoney(lead.asking_price)}</div>
      )}
    </button>
  );
}

function LeadModal({ lead, onClose, onSaved, onDelete, onConvert }) {
  const navigate = useNavigate();
  const isNew = !lead;
  const [busy, setBusy] = useState(false);
  const [confirmPrice, setConfirmPrice] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({
    seller_name: lead?.seller_name || "",
    seller_phone: lead?.seller_phone || "",
    seller_email: lead?.seller_email || "",
    property_address: lead?.property_address || "",
    asking_price: lead?.asking_price ?? "",
    motivation: lead?.motivation || "",
    status: lead?.status || "new",
    source: lead?.source || "",
    next_follow_up: lead?.next_follow_up || "",
    notes: lead?.notes || "",
  });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (confirmHighPrice = false) => {
    if (!form.seller_name.trim() || !form.property_address.trim()) {
      toast.error("Seller name and property address are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        asking_price: form.asking_price === "" ? null : Number(form.asking_price),
        source: form.source || null,
        next_follow_up: form.next_follow_up || null,
      };
      const params = confirmHighPrice ? { confirm_high_price: true } : undefined;
      const { data } = isNew
        ? await api.post("/leads", payload, { params })
        : await api.patch(`/leads/${lead.id}`, payload, { params });
      toast.success(isNew ? "Lead added" : "Lead updated");
      onSaved(data);
    } catch (e) {
      if (e?.response?.status === 409) {
        setConfirmPrice(e.response.data.detail);
      } else {
        toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
      }
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      onDelete(lead.id);
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  const canConvert = !isNew && form.status === "under_contract";

  const footer = (
    <div className="flex flex-wrap gap-2 justify-between items-center">
      <div>
        {!isNew && (
          <button onClick={() => setConfirmDelete(true)} disabled={busy} className="btn-ghost text-sm flex items-center gap-2" style={{ color: "#ef4444" }} data-testid="lead-delete-button">
            <Trash2 size={14} /> Delete
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {canConvert && (
          <button onClick={() => onConvert(lead.id)} className="text-sm flex items-center gap-2 px-4 py-2 rounded-2xl transition" style={{ background: "var(--lime-glow)", color: "var(--lime)", border: "1px solid var(--border-lime)" }} data-testid="lead-convert-button">
            <Send size={14} /> Submit Deal for JV
          </button>
        )}
        <button onClick={onClose} className="btn-ghost text-sm" data-testid="lead-cancel-button">Cancel</button>
        {!isNew && (
          <button
            onClick={() => {
              const q = [
                `Lead: ${form.property_address}`,
                `Stage: ${(form.status || "new").replace(/_/g, " ")}`,
                form.asking_price ? `Asking: $${Number(form.asking_price).toLocaleString()}` : null,
                form.motivation ? `Seller situation: ${form.motivation}` : null,
                form.notes ? `Notes: ${form.notes}` : null,
                "",
                "What should my next move be on this one?",
              ].filter(Boolean).join("\n");
              navigate(`/coach?ask=${encodeURIComponent(q)}`);
            }}
            className="btn-ghost text-sm flex items-center gap-2"
            data-testid="ask-coach-about-lead"
          >
            <Sparkles size={13} /> Ask coach
          </button>
        )}
        <button onClick={() => save(false)} disabled={busy} className="btn-primary text-sm" data-testid="lead-save-button">
          {busy ? "Saving..." : (isNew ? "Add Lead" : "Save Changes")}
        </button>
      </div>
    </div>
  );

  return (
    <BottomSheet
      open={true}
      onClose={onClose}
      title={isNew ? "Add Lead" : "Edit Lead"}
      footer={footer}
    >
      <div className="space-y-4" data-testid="lead-modal">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Seller name *" value={form.seller_name} onChange={(v) => update("seller_name", v)} testid="seller-name" />
            <Field label="Phone" value={form.seller_phone} onChange={(v) => update("seller_phone", v)} testid="seller-phone" />
            <Field label="Email" value={form.seller_email} onChange={(v) => update("seller_email", v)} testid="seller-email" type="email" />
            <Field label="Asking price ($)" value={form.asking_price} onChange={(v) => update("asking_price", v)} testid="asking-price" type="number" />
          </div>
          <Field label="Property address *" value={form.property_address} onChange={(v) => update("property_address", v)} testid="property-address" />
          <div className="grid sm:grid-cols-3 gap-3">
            <Select label="Status" value={form.status} onChange={(v) => update("status", v)} options={LEAD_STATUSES} testid="status" />
            <Select label="Source" value={form.source} onChange={(v) => update("source", v)} options={[{ value: "", label: "—" }, ...LEAD_SOURCES]} testid="source" />
            <div>
              <Field label="Next follow-up" value={form.next_follow_up} onChange={(v) => update("next_follow_up", v)} testid="followup" type="date" />
              <p className="text-[10px] mt-1" style={{ color: "var(--text-dim)" }}>
                Auto-set to 7 days out. Resets when you move the lead.
              </p>
            </div>
          </div>
          <Field label="Seller motivation" value={form.motivation} onChange={(v) => update("motivation", v)} testid="motivation" placeholder="Why are they selling? (divorce, job, tired landlord...)" textarea />
          <Field label="Notes" value={form.notes} onChange={(v) => update("notes", v)} testid="notes" placeholder="Top-line facts you always want visible (spouse's name, gate code, etc)" textarea />

          {!isNew && (
            <div className="pt-4 mt-2" style={{ borderTop: "1px solid var(--border)" }}>
              <LeadAppointments leadId={lead.id} leadName={form.seller_name} />
            </div>
          )}

          {!isNew && (
            <div className="pt-4 mt-2" style={{ borderTop: "1px solid var(--border)" }}>
              <LeadTasks leadId={lead.id} />
            </div>
          )}

          {!isNew && (
            <div className="pt-4 mt-2" style={{ borderTop: "1px solid var(--border)" }}>
              <LeadActivityLog leadId={lead.id} onLeadUpdated={() => onSaved({ ...lead, ...form })} />
            </div>
          )}
      </div>
      <ConfirmDialog
        open={!!confirmPrice}
        title="Is that price right?"
        description={confirmPrice || ""}
        confirmLabel="Yes, save it"
        onCancel={() => setConfirmPrice(null)}
        onConfirm={() => { setConfirmPrice(null); save(true); }}
      />
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this lead?"
        description="This lead and all its notes will be permanently removed. This can't be undone."
        confirmLabel="Delete lead"
        destructive
        onCancel={() => setConfirmDelete(false)}
        onConfirm={del}
      />
    </BottomSheet>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder, textarea }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {textarea ? (
        <textarea
          className="input-base"
          rows={2}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={`lead-${testid}-input`}
        />
      ) : (
        <input
          className="input-base"
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={`lead-${testid}-input`}
        />
      )}
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <select
        className="input-base"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`lead-${testid}-select`}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function PrivacyModal({ onAck, onClose }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  return createPortal((
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="privacy-modal"
    >
      <div
        className="w-full max-w-md rounded-xl p-8"
        style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center mb-5">
          <div className="p-3 rounded-full" style={{ background: "var(--lime-glow)" }}>
            <Lock size={22} style={{ color: "var(--lime)" }} />
          </div>
        </div>
        <h2 className="h-display text-2xl text-center mb-3">Your leads are private</h2>
        <p className="text-sm text-center mb-6" style={{ color: "var(--text-muted)" }}>
          Before you add anything: your coach <strong style={{ color: "var(--text)" }}>cannot see</strong> the leads
          or seller information you put in this pipeline. This is your private workspace to track follow-ups,
          conversations, and motivated sellers, only visible to you on your account.
        </p>
        <p className="text-xs text-center mb-6 font-mono" style={{ color: "var(--text-dim)" }}>
          When a lead becomes a deal you want help on, you can use the <strong style={{ color: "var(--lime)" }}>Submit Deal for JV</strong> button to share it.
        </p>
        <button
          onClick={onAck}
          className="btn-primary w-full"
          data-testid="privacy-ack-button"
        >
          Got it. Let me add my lead
        </button>
      </div>
    </div>
  ), document.body);
}
